
import { Grid, User, LogOut, PhoneCall } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useEffect, useState } from "react";
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarTrigger,
} from "@/components/ui/menubar";
import { Button } from "@/components/ui/button"; 

interface TopMenuProps {
  onboardingStage?: string | null; // Optional: null or string
  onTestCallClick?: () => Promise<void> | void; // Optional: function returning void or promise<void>
}

export function TopMenu({ onboardingStage: propOnboardingStage, onTestCallClick }: TopMenuProps) { 
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [planId, setPlanId] = useState<number | null>(null);
  const [localOnboardingStage, setLocalOnboardingStage] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        // Check localStorage first
        const storedPlan = localStorage.getItem('userPlan');
        if (storedPlan) {
          setPlanId(Number(storedPlan));
          return;
        }

        // Fetch onboarding stage
        const storedOnboarding = localStorage.getItem('onboardingStage');
        if (storedOnboarding) {
          setLocalOnboardingStage(storedOnboarding);
          return;
        }

        const subscriber_id = localStorage.getItem('subscriber_id');

        if (!subscriber_id) {
          console.error('No subscriber_id found in localStorage');
          return;
        }

        const { data, error } = await supabase
          .from('subscribers')
          .select('plan_id_oneinbox, onboarding_stage')
          .eq('subscriber_id', subscriber_id) 
          .maybeSingle();

        if (!error && data) {
          // Set plan ID
          const plan = Number(data.plan_id_oneinbox);
          setPlanId(plan);
          localStorage.setItem('userPlan', plan.toString());
          
          // Set onboarding stage
          setLocalOnboardingStage(data.onboarding_stage);
          localStorage.setItem('onboardingStage', data.onboarding_stage);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchUserData();
  }, []);


  // Determine which onboarding stage to use
  const effectiveOnboardingStage = propOnboardingStage || localOnboardingStage;

  const handlePreferencesNavigation = () => {
    // Determine destination based on plan ID
    const destination = planId === 117 ? '/time-preference' : 
                       planId === 118 ? '/time-preference-2' : null;
    
    if (destination) {
      handleNavigationWithValidation(destination);
    } else {
      toast({
        title: "Plan Error",
        description: "Could not determine your plan type",
        variant: "destructive",
      });
    }
  };

  const handleNavigationWithValidation = (destination: string) => {
    // Only perform validation if currently on a time preference page
    if (location.pathname.includes('time-preference')) {
      try {
        const storedPrefs = localStorage.getItem('userPreferences');
        if (storedPrefs) {
          const preferences = JSON.parse(storedPrefs);
          const isAnyDayActive = Object.values(preferences).some((pref: any) => pref.active);
          
          if (!isAnyDayActive) {
            toast({
              title: "Validation Error",
              description: "Please enable at least 1 day for scheduling before leaving this page",
              variant: "destructive",
            });
            return; // Prevent navigation
          }
        }
      } catch (error) {
        console.error('Error checking preferences:', error);
      }
    }
    
    // If validation passes or not on time preference page, navigate
    navigate(destination);
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      toast({
        title: "Logged out successfully",
        description: "You have been logged out of your account",
      });
      navigate("/");
    } catch (error) {
      console.error("Error logging out:", error);
      toast({
        title: "Error logging out",
        description: "There was a problem logging out. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Updated test call button visibility
  const showTestCallButton = location.pathname === "/test-call" && 
    ['payment_complete', 'active', 'inactive'].includes(effectiveOnboardingStage || '');

  return (
    <div className="w-full border-b mb-4">
      <div className="flex justify-between items-center px-2 lg:px-4">
        <Menubar className="border-none p-0">
          <MenubarMenu>
            <MenubarTrigger 
              className="cursor-pointer"
              onClick={() => handleNavigationWithValidation("/call-history")}
            >
              <Grid className="mr-2 h-4 w-4" />
              Dashboard
            </MenubarTrigger>
          </MenubarMenu>

          <MenubarMenu>
            <MenubarTrigger className="cursor-pointer">
              <User className="mr-2 h-4 w-4" />
              Profile
            </MenubarTrigger>
            <MenubarContent>
              <MenubarItem onClick={() => handleNavigationWithValidation("/settings")}>Settings</MenubarItem>
              <MenubarItem onClick={handlePreferencesNavigation}>Preferences</MenubarItem>
            </MenubarContent>
          </MenubarMenu>

          <MenubarMenu>
            <MenubarTrigger 
              className="cursor-pointer"
              onClick={handleLogout}
            >
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </MenubarTrigger>
          </MenubarMenu>
        </Menubar>
      
        {/* Conditionally render the Test Call button on the right */}
        {showTestCallButton && onTestCallClick && (
            <Button onClick={onTestCallClick} size="sm" className="ml-auto"> {/* Use ml-auto to push right */}
              <PhoneCall className="mr-2 h-4 w-4" />
              Test Call
            </Button>
          )}
      </div>
    </div>
  );
}
